import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, inject, Inject, input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { Observable, filter, share, startWith, switchMap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AcvisService } from '@app/services/acvis.service';
import { HotelFiltersService } from '@app/services/hotelfilters.service';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { Airport } from '@app/models/hotels/Airport';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MediaService } from '@app/services/media.service';

@Component({
    selector: 'clever-airport',
    templateUrl: './airport.component.html',
    styleUrls: ['./airport.component.scss'],
    standalone: false
})
export class AirportComponent implements OnInit {
    mode = input<string>('');
    ziel = input<string>('Abflughafen');
    title = input<string>('Beliebig');
    inAll = input<boolean>(false);
    isBrowser = signal<boolean>(false);
    showModal = signal<boolean>(false);
    focus = signal<boolean>(false);
    zielText = signal<string>('Beliebig');
    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList = signal<Airport[]>([]);


    tmp_nord: any[] = [];
    tmp_sud: any[] = [];
    tmp_ost: any[] = [];
    tmp_west: any[] = [];

    flughafen_nord = signal<number>(-1);
    flughafen_sud = signal<number>(-1);
    flughafen_ost = signal<number>(-1);
    flughafen_west = signal<number>(-1);

    destroyRef = inject(DestroyRef);

    // Airports static
    de_flughafen = [
        {
            name: 'Berlin-Brandenburg',
            code: 'BER',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Bremen',
            code: 'BRE',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Dortmund',
            code: 'DTM',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Dresden',
            code: 'DRS',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Düsseldorf',
            code: 'DUS',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Erfurt',
            code: 'ERF',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Frankfurt am Main',
            code: 'FRA',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Frankfurt-Hahn',
            code: 'HHN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Friedrichshafen',
            code: 'FDH',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Hamburg',
            code: 'HAM',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Hannover',
            code: 'HAJ',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Karlsruhe / Baden-Baden',
            code: 'FKB',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Kassel',
            code: 'KSF',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Köln-Bonn',
            code: 'CGN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Leipzig/HBeliebig',
            code: 'LEJ',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Lübeck',
            code: 'LBC',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Magdeburg / Cochstedt',
            code: 'CSO',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Memmingen',
            code: 'FMM',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'München',
            code: 'MUC',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Münster-Osnabrück',
            code: 'FMO',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Nürnberg',
            code: 'NUE',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Paderborn',
            code: 'PAD',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Rostock',
            code: 'RLG',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Saarbrücken',
            code: 'SCN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Stuttgart',
            code: 'STR',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Weeze / Niederrhein',
            code: 'NRN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Westerland/Sylt',
            code: 'GWT',
            region: 'nord',
            countryCode: 'DE'
        },
    ];

    fr_flughafen = [
        {
            name: 'Strasbourg',
            code: 'SXB',
            countryCode: 'FR'
        },
    ];

    es_flughafen = [
        {
            name: 'Mallorca',
            code: 'PMI',
            countryCode: 'ES'
        },
    ];

    lu_flughafen = [
        {
            name: 'Luxemburg',
            code: 'LUX',
            countryCode: 'LU'
        },
    ];

    beliebig_flughafen = [
        {
            name: 'Berlin',
            code: 'BER',
            countryCode: 'DE'
        },
        {
            name: 'Düsseldorf',
            code: 'DUS',
            countryCode: 'DE'
        },
        {
            name: 'Frankfurt',
            code: 'FRA',
            countryCode: 'DE'
        },
        {
            name: 'München',
            code: 'MUC',
            countryCode: 'DE'
        },
    ];

    be_flughafen = [
        {
            name: 'Brüssel',
            code: 'BRU',
            countryCode: 'BE'
        },
        {
            name: 'Charleroi',
            code: 'CRL',
            countryCode: 'BE'
        },
        {
            name: 'Lüttich',
            code: 'LGG',
            countryCode: 'BE'
        },
    ];

    nl_flughafen = [
        {
            name: 'Amsterdam',
            code: 'AMS',
            countryCode: 'NL'
        },
        {
            name: 'Eindhoven',
            code: 'EIN',
            countryCode: 'NL'
        },
        {
            name: 'Groningen',
            code: 'GRQ',
            countryCode: 'NL'
        },
        {
            name: 'Maastricht',
            code: 'MST',
            countryCode: 'NL'
        },
    ];

    at_flughafen = [
        {
            name: 'Graz',
            code: 'GRZ',
            countryCode: 'AT'
        },
        {
            name: 'Innsbruck',
            code: 'INN',
            countryCode: 'AT'
        },
        {
            name: 'Klagenfurt',
            code: 'KLU',
            countryCode: 'AT'
        },
        {
            name: 'Linz',
            code: 'LNZ',
            countryCode: 'AT'
        },
        {
            name: 'Salzburg',
            code: 'SZG',
            countryCode: 'AT'
        },
        {
            name: 'Wien',
            code: 'VIE',
            countryCode: 'AT'
        }
    ];

    ch_flughafen = [
        {
            name: 'Zürich',
            code: 'ZRH',
            countryCode: 'CH'
        },
        {
            name: 'Basel-Mulhouse',
            code: 'BSL',
            countryCode: 'CH'
        },
        {
            name: 'Bern',
            code: 'BRN',
            countryCode: 'CH'
        },
        {
            name: 'Genf',
            code: 'GVA',
            countryCode: 'CH'
        },
        {
            name: 'St.GBeliebign/Altenrhein',
            code: 'ACH',
            countryCode: 'CH'
        },
    ];

    pl_flughafen = [
        {
            name: 'Krakau',
            code: 'KRK',
            countryCode: 'PL'
        },
        {
            name: 'Warschau',
            code: 'WAW',
            countryCode: 'PL'
        }
    ];

    dk_flughafen = [
        {
            name: 'Billund',
            code: 'BLL',
            countryCode: 'DK'
        }
    ];

    cz_flughafen = [
        {
            name: 'Prag',
            code: 'PRG',
            countryCode: 'CZ'
        }
    ];

    overlaypos: ConnectedPosition[] = [
        {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
        }
    ];

    constructor(
        protected mediaService: MediaService,
        protected acvisService: AcvisService,
        protected hotelService: HotelFiltersService,
        @Inject(PLATFORM_ID) protected platformId: any,
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
    }

    ngOnInit(): void {
        this.zielText.set(this.title());
        if (this.isBrowser()) {
            this.initSearchChange();
            this.initStaticAirports();
        }
    }

    async initStaticAirports() {
        for (let i = 0; i < this.de_flughafen.length; i++) {
            if (this.de_flughafen[i].region === 'sud') {
                this.tmp_sud.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'nord') {
                this.tmp_nord.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'ost') {
                this.tmp_ost.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'west') {
                this.tmp_west.push(this.de_flughafen[i]);
            }
        }
        this.initFilterSub();
    }

    initSearchChange() {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            // debounceTime(100),
            filter(value => (typeof value === 'string' && value?.length > 0)),
            share()
        );

        this.searchObj = filterShare.pipe(
            switchMap(value => this.acvisService.request({
                searchValue: value,
                inAll: this.inAll()
            }, 'airoportListCompletions'))
        );
    }

    initFilterSub() {
        this.hotelService.getfilters$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((filter: ParamFilters) => {
            this.initFilter(filter);
            this.checkRegion();
        });
    }

    async initFilter(filter) {
        if (this.inAll()) {
            if (filter?.arrivalAirportList?.length > 0) {
                if (JSON.stringify(this.selectedList()) !== JSON.stringify(filter?.arrivalAirportList)) {
                    this.selectedList.set(JSON.parse(JSON.stringify(filter?.arrivalAirportList)));
                    this.zielText.set(this.selectedList().length + ' ' + this.ziel());
                }
            } else {
                this.zielText.set(this.title());
                this.selectedList.set([]);
            }
        } else {
            if (filter?.airportsResult?.length > 0) {
                if (JSON.stringify(this.selectedList()) !== JSON.stringify(filter?.airportsResult)) {
                    this.selectedList.set(JSON.parse(JSON.stringify(filter?.airportsResult)));
                    this.zielText.set(this.selectedList().length + ' ' + this.ziel());
                }
            } else {
                this.zielText.set(this.title());
                this.selectedList.set([]);
            }
        }
    }

    async checkRegion() {
        // let tmp_sud_ck = true;
        // for (let i = 0; i < this.tmp_sud.length; i++) {
        //     if (this.isNotInlist(this.tmp_sud[i])) {
        //         tmp_sud_ck = false;
        //         i = this.tmp_sud.length;
        //     }
        // }
        // this.flughafen_sud = tmp_sud_ck;

        const evrys = this.tmp_sud.every(val => { return !this.isNotInlist(val) });
        const somes = this.tmp_sud.some(val => { return !this.isNotInlist(val) });

        this.flughafen_sud.set(evrys ? 1 : somes ? 0 : -1);

        // let tmp_nord_ck = true;
        // for (let i = 0; i < this.tmp_nord.length; i++) {
        //     if(this.isNotInlist(this.tmp_nord[i])) {
        //         tmp_nord_ck = false;
        //         i = this.tmp_nord.length;
        //     }
        // }

        const evry = this.tmp_nord.every(val => { return !this.isNotInlist(val) });
        const some = this.tmp_nord.some(val => { return !this.isNotInlist(val) });

        this.flughafen_nord.set(evry ? 1 : some ? 0 : -1);

        const evryo = this.tmp_ost.every(val => { return !this.isNotInlist(val) });
        const someo = this.tmp_ost.some(val => { return !this.isNotInlist(val) });

        this.flughafen_ost.set(evryo ? 1 : someo ? 0 : -1);

        // let tmp_ost_ck = true;
        // for (let i = 0; i < this.tmp_ost.length; i++) {
        //     if (this.isNotInlist(this.tmp_ost[i])) {
        //         tmp_ost_ck = false;
        //         i = this.tmp_ost.length;
        //     }
        // }
        // this.flughafen_ost = tmp_ost_ck;

        const evryow = this.tmp_west.every(val => { return !this.isNotInlist(val) });
        const someow = this.tmp_west.some(val => { return !this.isNotInlist(val) });

        this.flughafen_west.set(evryow ? 1 : someow ? 0 : -1);

        // let tmp_west_ck = true;
        // for (let i = 0; i < this.tmp_west.length; i++) {
        //     if (this.isNotInlist(this.tmp_west[i])) {
        //         tmp_west_ck = false;
        //         i = this.tmp_west.length;
        //     }
        // }
        // this.flughafen_west = tmp_west_ck;
        // this.cdr.detectChanges();
    }

    async openModal() {
        this.showModal.set(true);
        this.focus.set(true);
    }

    async openModalMobile() {
        this.showModal.set(true);
        this.focus.set(true);
    }

    async closeModal() {
        this.showModal.set(false);
        this.mySearchControl.setValue('');
    }

    async closeModalMobile() {
        this.take();
    }

    async clearText() {
        this.mySearchControl.setValue('');
        this.focus.set(false);
        setTimeout(() => {
            this.focus.set(true);
        }, 50);
    }

    async selectAir(ev, item) {
        ev.stopPropagation();
        this.mySearchControl.setValue('');
        this.selectValue(item);
    }

    async selectValue(item) {
        this.selectedList().push({
            name: item.name,
            code: item.code,
        });
        this.zielText.set(this.selectedList().length + ' ' + this.ziel());
    }

    async removeValue(index) {
        this.selectedList().splice(index, 1);
        this.zielText.set(this.selectedList().length + ' ' + this.ziel());
    }

    async addAirport(ev, item) {
        ev.stopPropagation();
        const index = this.indexInlist(item);
        if (index >= 0) {
            this.removeValue(index);
        } else {
            this.selectValue(item);
        }
        this.checkRegion();
    }

    indexInlist(item): number {
        for (let i = 0; i < this.selectedList().length; i++) {
            if (this.selectedList()[i].code === item?.code) {
                return i;
            }
        }
        return -1;
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList()?.length; i++) {
            if (this.selectedList()[i].code === item?.code) {
                return false;
            }
        }
        return true;
    }

    async removeAllSelected(ev) {
        ev.stopPropagation();
        this.selectedList.set([]);
        this.zielText.set(this.title());
        this.flughafen_nord.set(-1);
        this.flughafen_sud.set(-1);
        this.flughafen_ost.set(-1);
        this.flughafen_west.set(-1);
    }

    async removeFromSelected(ev, k) {
        ev.stopPropagation();
        if (this.selectedList()[k]) {
            this.selectedList().splice(k, 1);
        }
        this.zielText.set(this.selectedList().length > 0 ? this.selectedList().length + ' ' + this.ziel() : this.title());
        this.checkRegion();
    }

    async take() {
        if (this.inAll()) {
            this.hotelService.arrivalAirports(JSON.parse(JSON.stringify(this.selectedList())), this.mode() !== 'home');
        } else {
            this.hotelService.airportsResult(JSON.parse(JSON.stringify(this.selectedList())), this.mode() !== 'home');
        }
        this.closeModal();
    }

    checkNord(ev): void {
        ev.stopPropagation();
        this.flughafen_nord.set(this.flughafen_nord() === -1 || this.flughafen_nord() === 0 ? 1 : -1);
        for (let i = 0; i < this.tmp_nord.length; i++) {
            const id = this.indexInlist(this.tmp_nord[i]);
            if (this.flughafen_nord() !== 1 && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_nord() === 1 && id < 0) {
                this.selectValue(this.tmp_nord[i]);
            }
        }
    }

    checkSud(ev): void {
        ev.stopPropagation();
        this.flughafen_sud.set(this.flughafen_sud() === -1 || this.flughafen_sud() === 0 ? 1 : -1);
        for (let i = 0; i < this.tmp_sud.length; i++) {
            const id = this.indexInlist(this.tmp_sud[i]);
            if (this.flughafen_sud() !== 1 && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_sud() === 1 && id < 0) {
                this.selectValue(this.tmp_sud[i]);
            }
        }
    }

    checkOst(ev): void {
        ev.stopPropagation();
        this.flughafen_ost.set(this.flughafen_ost() === -1 || this.flughafen_ost() === 0 ? 1 : -1);
        for (let i = 0; i < this.tmp_ost.length; i++) {
            const id = this.indexInlist(this.tmp_ost[i]);
            if (this.flughafen_ost() !== 1 && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_ost() === 1 && id < 0) {
                this.selectValue(this.tmp_ost[i]);
            }
        }
    }

    checkWest(ev): void {
        ev.stopPropagation();
        this.flughafen_west.set(this.flughafen_west() === -1 || this.flughafen_west() === 0 ? 1 : -1);
        for (let i = 0; i < this.tmp_west.length; i++) {
            const id = this.indexInlist(this.tmp_west[i]);
            if (this.flughafen_west() !== 1 && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_west() === 1 && id < 0) {
                this.selectValue(this.tmp_west[i]);
            }
        }
    }
}
